import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import useNavigationService from 'hooks/useNavigationService';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { RootState } from 'redux/store';
import { blocosCategoriaDisponiveis } from 'utils/constants/blocosCategoriaDisponiveis';
import { formatCnpjCpf } from 'utils/formatUtils';
import { alertDefaultConfig } from 'utils/toastifyUtils';
import { Status, StatusEditavel } from 'utils/types/arquivoEditavel';
import { IBlocoRegistroCategoria } from 'utils/types/blocoCategoria';
import { FiltroRegistro } from 'utils/types/filtroRegistro';
import { ObrigacaoDescricao } from 'utils/types/obrigacao';
import { IRegistroExibicao } from 'utils/types/registroExibicao';

import { Check, Error as ErrorIcon, Info } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Grid2 as Grid,
  Typography,
} from '@mui/material';

import AlertModal from 'components/AlertModal/AlertModal';
import BackdropLoading from 'components/BackdropLoading';
import CardMainContainer from 'components/CardMainContainer';
import CustomSelect from 'components/CustomSelect/CustomSelect';
import {
  useCancelarEdicao,
  useFinalizarEdicao,
} from 'components/DataTableArquivosEditaveis/arquivosEditaveis.service';
import TabsNavigation from 'components/TabsNavigation';

import { IBlocoRegistroCategoriaFilter } from './filters';
import {
  fetchArquivosStatus,
  fetchBlocoCategoria,
  fetchDadosArquivoEditavel,
  fetchRegistroCategoria,
  fetchRegistros,
} from './loaderFile.service';

const LoaderFile = () => {
  const { processamentoSpedID: processamentoID } = useParams<{
    processamentoSpedID: string;
  }>();

  const { id: usuarioID } = useSelector(
    (state: RootState) => state.token.usuario,
  );

  const queryClient = useQueryClient();

  const { navegarParaListagem } = useNavigationService();

  const [labelEmpresaClienteFormatada, setLabelEmpresaClienteFormatada] =
    useState<string>('');

  const [modalCancelationIsOpen, setModalCancelationIsOpen] =
    useState<boolean>(false);
  const handleCancelationModal = () =>
    setModalCancelationIsOpen(!modalCancelationIsOpen);

  const [modalFinalizarIsOpen, setModalFinalizarIsOpen] = useState(false);

  const cancelarEdicaoMutation = useCancelarEdicao(queryClient);
  const finalizarEdicaoMutation = useFinalizarEdicao(queryClient);

  const cancelarEdicao = async () => {
    try {
      if (!processamentoID) {
        throw new Error('Processamento ID não encontrado.');
      }
      await cancelarEdicaoMutation.mutateAsync(Number(processamentoID));
      navegarParaListagem();
      toast.success('Edição cancelada com sucesso!');
    } catch (error) {
      console.error('Erro ao cancelar edição:', error);
      navegarParaListagem();
      toast.error('Erro ao cancelar edição. Tente novamente mais tarde.');
    }
  };

  const handleFinalizarEdicao = async () => {
    try {
      if (!processamentoID) {
        throw new Error('Processamento ID não encontrado.');
      }

      await finalizarEdicaoMutation.mutateAsync({
        processamentoID: Number(processamentoID),
      });
      navegarParaListagem();
      toast.success('Arquivo finalizado com sucesso!');
    } catch (error) {
      console.error('Erro ao finalizar edição:', error);
      toast.error('Erro ao finalizar arquivo.');
    }
  };

  const formik = useFormik<IBlocoRegistroCategoriaFilter>({
    initialValues: {
      blocoCategoriaID: null,
      registroCategoriaID: null,
    },
    onSubmit: () => {
      refetchRegistros();
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const {
    data: arquivoEditavel,
    isPending: isPendingArquivoEditavel,
    isError: isErrorArquivoEditavel,
  } = useQuery({
    queryKey: ['arquivoEditavel', usuarioID, processamentoID],
    queryFn: fetchDadosArquivoEditavel,
    enabled: !!processamentoID && !!usuarioID,
  });

  useEffect(() => {
    if (!!arquivoEditavel) {
      const formattedLabel = `${formatCnpjCpf(
        arquivoEditavel.empresaClienteNumeroRegistro,
      )} - ${arquivoEditavel.empresaClienteNomeEmpresarial}`;
      setLabelEmpresaClienteFormatada(formattedLabel);
    }
  }, [arquivoEditavel]);

  const { data: statusArquivo } = useQuery({
    queryKey: ['statusArquivo', processamentoID],
    queryFn: fetchArquivosStatus,
    enabled: !!processamentoID,
    staleTime: 1000 * 5,
    refetchInterval: (query) => {
      const data = query.state.data;
      return !StatusEditavel.includes(data ?? '') ? 1000 * 15 : false;
    },
  });

  const mensagemArquivoCarregando =
    'Aguarde, o arquivo está sendo carregado e em breve estará disponível para edição!';

  const mensagemArquivoDisponivel =
    'Seu arquivo já está pronto para ser editado! Selecione o bloco e o registro que deseja editar nos filtros acima.';

  const mensagemArquivoFalha =
    'Ocorreu um problema ao disponibilizar a edição do arquivo.';

  const {
    data: dadosBlocoCategoria,
    isPending: isPendingBlocoCategoria,
    isError: isErrorBlocoCategoria,
  } = useQuery({
    queryKey: ['blocoCategoria'],
    queryFn: fetchBlocoCategoria,
    enabled: !!processamentoID && !!usuarioID,
  });

  const {
    data: dadosRegistroCategoria,
    isPending: isPendingRegistroCategoria,
    isError: isErrorRegistroCategoria,
  } = useQuery({
    queryKey: ['registroCategoria', formik.values.blocoCategoriaID],
    queryFn: fetchRegistroCategoria,
    enabled: !!formik.values.blocoCategoriaID,
  });

  const {
    data: dadosRegistros,
    isPending: isPendingRegistros,
    isError: isErrorRegistros,
    refetch: refetchRegistros,
  } = useQuery({
    queryKey: [
      'registros',
      formik.values.registroCategoriaID,
      formik.values.blocoCategoriaID,
    ],
    queryFn: fetchRegistros,
    enabled: false,
  });

  const blocoCategoriaOptions = dadosBlocoCategoria?.map((bloco) => ({
    label: bloco.descricao,
    value: bloco.categoriaID,
    disabled:
      !blocosCategoriaDisponiveis.includes(bloco.categoriaID) ||
      bloco.descricao.includes('Bloco D'), // Condição temporária para bloquear o Bloco D
  }));

  const registroCategoriaOptions = dadosRegistroCategoria?.map((registro) => ({
    label: registro.descricao,
    value: registro.categoriaID,
  }));

  const isLoading = isPendingArquivoEditavel || isPendingBlocoCategoria;
  const isError =
    isErrorArquivoEditavel || isErrorBlocoCategoria || isErrorRegistroCategoria;
  useEffect(() => {
    if (isError) {
      toast.error('Erro ao consultar arquivos editáveis.', alertDefaultConfig);
    }

    if (isErrorRegistros) {
      toast.error('Erro ao consultar registros.', alertDefaultConfig);
    }
  }, [isError, isErrorRegistros]);

  const [filtroPorRegistro, setFiltroPorRegistro] = useState<FiltroRegistro[]>(
    [],
  );

  const defineTabsNavigationKey = (dadosRegistros: IRegistroExibicao[]) => {
    const modificadoresBlocoKey = dadosBlocoCategoria
      ?.find(
        (bloco: IBlocoRegistroCategoria) =>
          bloco.categoriaID === formik.values.blocoCategoriaID,
      )
      ?.modificadoresCategoria.map((modificador) => modificador.id)
      .join('');
    const modificadoresRegistroKey = dadosRegistroCategoria
      ?.find(
        (registro: IBlocoRegistroCategoria) =>
          registro.categoriaID === formik.values.registroCategoriaID,
      )
      ?.modificadoresCategoria.map((modificador) => modificador.id)
      .join('');
    const registroIdsKey = dadosRegistros
      .map((dadosRegistro: IRegistroExibicao) => dadosRegistro.id)
      .join('');
    return `${modificadoresBlocoKey}-${modificadoresRegistroKey}-${registroIdsKey}`;
  };

  return (
    <>
      {isLoading && !isError && <BackdropLoading />}
      <AlertModal
        open={modalCancelationIsOpen}
        onClose={handleCancelationModal}
        confirmButtonAction={async () => await cancelarEdicao()}
        title="Atenção"
        content="Ao cancelar a edição de um arquivo você perderá todas as alterações realizadas até o momento, caso deseje editar novamente este arquivo pode levar alguns minutos para carregamento dos dados."
        additionalInfo="Deseja continuar?"
        cancelButtonText="Não"
        confirmButtonText="Sim"
      />

      <AlertModal
        open={modalFinalizarIsOpen}
        onClose={() => setModalFinalizarIsOpen(false)}
        confirmButtonAction={async () => {
          await handleFinalizarEdicao();
          setModalFinalizarIsOpen(false);
        }}
        title="Atenção"
        content="Ao salvar as alterações este arquivo ficará disponível para edição por outros usuários responsáveis. Após o salvamento será necessário executar a ação de reprocessar arquivo. O arquivo editado ficará disponível para download no Painel Gerencial após processamento."
        additionalInfo="Deseja continuar?"
        cancelButtonText="Não"
        confirmButtonText="Sim"
      />
      <CardMainContainer title="Editor de Arquivo Fiscal">
        <Grid container spacing={0.5} mb={1}>
          <Grid size={12} mb={3}>
            <Typography
              variant="body1"
              style={{
                fontSize: '14px',
                display: 'flex',
              }}
              color="#8A8A8A"
            >
              <Info
                fontSize="small"
                style={{ marginRight: 4 }}
                color="secondary"
              />
              <strong>Atenção: </strong>&nbsp;Você pode filtrar e selecionar o
              registro que será alterado
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography
              variant="body1"
              style={{
                fontSize: '14px',
                display: 'flex',
              }}
              color="primary"
            >
              <strong>Empresa: </strong>&nbsp; {labelEmpresaClienteFormatada}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography
              variant="body1"
              style={{
                fontSize: '14px',
                display: 'flex',
              }}
              color="primary"
            >
              <strong>Obrigação: </strong>&nbsp;{' '}
              {ObrigacaoDescricao[arquivoEditavel?.obrigacaoID || 1]}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography
              variant="body1"
              style={{
                fontSize: '14px',
                display: 'flex',
              }}
              color="primary"
            >
              <strong>Competência: </strong>&nbsp;{' '}
              {dayjs(arquivoEditavel?.competencia).format('MM/YYYY')}
            </Typography>
          </Grid>
          <Grid size={12} mb={4}>
            <Typography
              variant="body1"
              style={{
                fontSize: '14px',
                display: 'flex',
              }}
              color="primary"
            >
              <strong>Tipo do arquivo: </strong>&nbsp;{' '}
              {arquivoEditavel?.tipoArquivo}
            </Typography>
          </Grid>

          <Grid size={3} mr={4}>
            <CustomSelect
              setValue={(value) => {
                formik.setFieldValue('blocoCategoriaID', value);
              }}
              id="blocoCategoriaID"
              name="blocoCategoriaID"
              value={formik.values.blocoCategoriaID ?? ''}
              options={blocoCategoriaOptions || []}
              label="Informe o Bloco:"
              placeholder="Selecione o Bloco"
              disabled={
                !!isLoading ||
                !arquivoEditavel ||
                !StatusEditavel.includes(statusArquivo ?? '')
              }
            />
          </Grid>

          <Grid size={3} mr={4}>
            <CustomSelect
              setValue={(value) => {
                formik.setFieldValue('registroCategoriaID', value);
              }}
              id="registroCategoriaID"
              name="registroCategoriaID"
              value={formik.values.registroCategoriaID ?? ''}
              options={registroCategoriaOptions || []}
              label="Informe o Registro:"
              placeholder="Selecione o Registro"
              disabled={
                !!isLoading ||
                isPendingRegistroCategoria ||
                !formik.values.blocoCategoriaID ||
                !arquivoEditavel ||
                !StatusEditavel.includes(statusArquivo ?? '')
              }
            />
          </Grid>
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => formik.handleSubmit()}
              disabled={
                !!isLoading ||
                !formik.values.blocoCategoriaID ||
                !formik.values.registroCategoriaID
              }
              sx={{ alignSelf: 'flex-end' }}
            >
              Buscar
            </Button>
          </Grid>
          {!!filtroPorRegistro.length && (
            <Grid size={3.5} ml={4}>
              <Typography
                variant="body1"
                style={{
                  fontSize: '14px',
                  display: 'flex',
                }}
                color="#8A8A8A"
              >
                <Info
                  fontSize="small"
                  style={{ marginRight: 4 }}
                  color="warning"
                />
                <strong>Atenção: </strong>&nbsp;A filtragem por linhas está
                ativa no momento.
              </Typography>
            </Grid>
          )}
        </Grid>
        {!!processamentoID && !!dadosRegistros && !isPendingRegistros ? (
          <TabsNavigation
            dadosAbas={dadosRegistros}
            processamentoSpedId={processamentoID}
            filtroPorRegistro={filtroPorRegistro}
            setFiltroPorRegistro={setFiltroPorRegistro}
            paiLoaderId={0}
            categoriaBlocoId={formik.values.blocoCategoriaID ?? undefined}
            key={defineTabsNavigationKey(dadosRegistros)}
            modificadoresCategoriaBloco={
              dadosBlocoCategoria?.find(
                (bloco: IBlocoRegistroCategoria) =>
                  bloco.categoriaID === formik.values.blocoCategoriaID,
              )?.modificadoresCategoria
            }
            modificadoresCategoriaRegistro={
              dadosRegistroCategoria?.find(
                (registro: IBlocoRegistroCategoria) =>
                  registro.categoriaID === formik.values.registroCategoriaID,
              )?.modificadoresCategoria
            }
          />
        ) : (
          <Grid
            container
            spacing={2}
            mt={5}
            ml={3}
            justifyContent={'flex-start'}
          >
            <Grid>
              {StatusEditavel.includes(statusArquivo ?? '') ? (
                <Check color="success" />
              ) : Status.Falha === statusArquivo ? (
                <ErrorIcon color="error" />
              ) : (
                <CircularProgress size={25} />
              )}
            </Grid>
            <Grid>
              <Typography variant="body1" color="primary" fontSize="14px">
                <strong>
                  {StatusEditavel.includes(statusArquivo ?? '')
                    ? mensagemArquivoDisponivel
                    : Status.Falha === statusArquivo
                      ? mensagemArquivoFalha
                      : mensagemArquivoCarregando}
                </strong>
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2} justifyContent="flex-end" mt={2}>
          <Grid>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleCancelationModal}
              disabled={!!isLoading}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setModalFinalizarIsOpen(true)}
              disabled={!!isLoading}
            >
              Salvar Alterações
            </Button>
          </Grid>
        </Grid>
      </CardMainContainer>
    </>
  );
};

export default LoaderFile;
